/**
 * LOCATION: dashboard-business/src/pages/VerifyOTP.js
 * FA icons only — zero emojis, mobile responsive, matches design system
 * Features: paste support, auto-submit, countdown timer, resend
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = 'https://janimony-backend.onrender.com/api';

const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email    = location.state?.email;

  const [otp,      setOtp]      = useState(['', '', '', '', '', '']);
  const [loading,  setLoading]  = useState(false);
  const [resending,setResending]= useState(false);
  const [error,    setError]    = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 min
  const [resent,   setResent]   = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) { navigate('/forgot-password'); return; }
    // Font Awesome
    if (!document.getElementById('fa-otp')) {
      const fa = document.createElement('link');
      fa.id = 'fa-otp'; fa.rel = 'stylesheet';
      fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
      document.head.appendChild(fa);
    }
  }, [email, navigate]);

  // Countdown
  useEffect(() => {
    if (timeLeft <= 0) return;
    const t = setInterval(() => setTimeLeft(p => p - 1), 1000);
    return () => clearInterval(t);
  }, [timeLeft]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1); // only last digit
    setOtp(next);
    setError('');

    // Move focus forward
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all filled
    if (index === 5 && value) {
      const code = [...next.slice(0, 5), value].join('');
      if (code.length === 6) handleVerify(code);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0)  inputRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const digits = pasted.split('');
      setOtp(digits);
      setError('');
      inputRefs.current[5]?.focus();
      // Auto-submit
      setTimeout(() => handleVerify(pasted), 100);
    }
  };

  const handleVerify = async (code = otp.join('')) => {
    if (code.length !== 6) { setError('Please enter all 6 digits'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/auth/verify-otp`, { email, otp: code });
      if (res.data.success) {
        navigate('/reset-password', {
          state: { resetToken: res.data.resetToken, email },
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP code. Please try again.');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/auth/resend-otp`, { email });
      if (res.data.success) {
        setTimeLeft(600);
        setOtp(['', '', '', '', '', '']);
        setResent(true);
        inputRefs.current[0]?.focus();
        setTimeout(() => setResent(false), 4000);
      }
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const allFilled = otp.every(d => d !== '');
  const expired   = timeLeft <= 0;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Syne:wght@700;800&display=swap');
        *{box-sizing:border-box;}
        .otp-page{min-height:100vh;background:linear-gradient(135deg,#0a0a0a 0%,#1a0533 55%,#0d1117 100%);display:flex;align-items:center;justify-content:center;padding:20px;font-family:'DM Sans',sans-serif;position:relative;overflow:hidden;}
        .otp-orb1{position:fixed;width:420px;height:420px;border-radius:50%;background:radial-gradient(circle,rgba(102,126,234,0.15),transparent 70%);top:-140px;right:-80px;pointer-events:none;}
        .otp-orb2{position:fixed;width:300px;height:300px;border-radius:50%;background:radial-gradient(circle,rgba(118,75,162,0.12),transparent 70%);bottom:-100px;left:-60px;pointer-events:none;}
        .otp-card{width:100%;max-width:460px;background:rgba(255,255,255,0.04);backdrop-filter:blur(20px);border:1px solid rgba(255,255,255,0.1);border-radius:24px;padding:44px 40px;position:relative;z-index:1;box-shadow:0 40px 80px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.08);}
        .otp-logo{display:flex;align-items:center;justify-content:center;gap:10px;margin-bottom:28px;}
        .otp-logo-icon{width:42px;height:42px;border-radius:13px;background:linear-gradient(135deg,#667eea,#764ba2);display:flex;align-items:center;justify-content:center;box-shadow:0 8px 20px rgba(102,126,234,0.4);}
        .otp-logo-name{font-family:'Syne',sans-serif;font-size:20px;font-weight:800;color:#fff;}
        .otp-icon-wrap{width:64px;height:64px;border-radius:18px;background:rgba(102,126,234,0.15);border:1px solid rgba(102,126,234,0.25);display:flex;align-items:center;justify-content:center;margin:0 auto 20px;}
        .otp-title{font-family:'Syne',sans-serif;font-size:24px;font-weight:800;color:#fff;text-align:center;margin-bottom:8px;}
        .otp-sub{font-size:13px;color:rgba(255,255,255,0.4);text-align:center;line-height:1.7;margin-bottom:6px;}
        .otp-email{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;background:rgba(102,126,234,0.12);border:1px solid rgba(102,126,234,0.25);border-radius:20px;font-size:12px;font-weight:700;color:#a5b4fc;margin-bottom:28px;}
        .otp-alert{padding:12px 14px;border-radius:10px;font-size:13px;margin-bottom:18px;display:flex;align-items:flex-start;gap:8px;line-height:1.5;}
        .otp-err{background:rgba(239,68,68,0.1);border:1px solid rgba(239,68,68,0.3);color:#fca5a5;}
        .otp-ok{background:rgba(16,185,129,0.1);border:1px solid rgba(16,185,129,0.3);color:#6ee7b7;}
        /* OTP input boxes */
        .otp-boxes{display:flex;gap:10px;justify-content:center;margin-bottom:24px;}
        .otp-box{width:52px;height:60px;background:rgba(255,255,255,0.06);border:2px solid rgba(255,255,255,0.12);border-radius:14px;font-size:26px;font-weight:800;text-align:center;color:#fff;outline:none;font-family:'DM Sans',sans-serif;transition:all 0.2s;caret-color:#667eea;}
        .otp-box:focus{border-color:rgba(102,126,234,0.7);background:rgba(102,126,234,0.1);box-shadow:0 0 0 3px rgba(102,126,234,0.15);}
        .otp-box.filled{border-color:rgba(102,126,234,0.5);background:rgba(102,126,234,0.08);}
        .otp-box.error{border-color:rgba(239,68,68,0.5);background:rgba(239,68,68,0.05);}
        /* Timer */
        .otp-timer{text-align:center;padding:12px 16px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.07);border-radius:12px;margin-bottom:20px;font-size:13px;color:rgba(255,255,255,0.4);display:flex;align-items:center;justify-content:center;gap:8px;}
        .otp-timer-val{font-weight:800;font-size:15px;}
        /* Verify button */
        .otp-btn{width:100%;padding:13px;background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;border:none;border-radius:12px;font-size:15px;font-weight:700;cursor:pointer;font-family:'DM Sans',sans-serif;display:flex;align-items:center;justify-content:center;gap:8px;box-shadow:0 8px 20px rgba(102,126,234,0.35);transition:all 0.2s;margin-bottom:16px;}
        .otp-btn:hover:not(:disabled){transform:translateY(-1px);}
        .otp-btn:disabled{opacity:0.5;cursor:not-allowed;}
        .otp-resend{text-align:center;font-size:13px;color:rgba(255,255,255,0.35);margin-bottom:16px;}
        .otp-resend-btn{background:transparent;border:none;cursor:pointer;color:#a5b4fc;font-weight:700;font-size:13px;font-family:'DM Sans',sans-serif;text-decoration:underline;}
        .otp-resend-btn:disabled{opacity:0.5;cursor:not-allowed;}
        .otp-back{display:block;text-align:center;font-size:13px;color:rgba(102,126,234,0.85);text-decoration:none;font-weight:700;margin-top:4px;}
        .otp-tips{background:rgba(102,126,234,0.08);border:1px solid rgba(102,126,234,0.2);border-radius:12px;padding:14px 16px;margin-top:20px;}
        .otp-tips-title{font-size:12px;font-weight:700;color:rgba(255,255,255,0.5);margin-bottom:8px;display:flex;align-items:center;gap:6px;}
        .otp-tips-item{font-size:12px;color:rgba(255,255,255,0.3);line-height:1.8;padding-left:4px;}
        @media(max-width:500px){
          .otp-card{padding:32px 20px;border-radius:20px;}
          .otp-box{width:44px;height:52px;font-size:22px;border-radius:11px;}
          .otp-boxes{gap:7px;}
          .otp-title{font-size:20px;}
        }
        @media(max-width:380px){
          .otp-box{width:38px;height:46px;font-size:20px;}
          .otp-boxes{gap:5px;}
        }
      `}</style>

      <div className="otp-page">
        <div className="otp-orb1" /><div className="otp-orb2" />

        <div className="otp-card">
          {/* Logo */}
       <div className="otp-logo">
  <img 
    src={`${process.env.PUBLIC_URL}/janimony-logo.png`} 
    alt="Janimony" 
    style={{ width: '100px', height: 'auto', objectFit: 'contain' }}
  />
</div>

          {/* Icon */}
          <div className="otp-icon-wrap">
            <i className="fa-solid fa-shield-halved" style={{ color: '#a5b4fc', fontSize: 28 }}></i>
          </div>

          <h1 className="otp-title">Enter OTP Code</h1>
          <p className="otp-sub">We sent a 6-digit code to:</p>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <span className="otp-email">
              <i className="fa-solid fa-envelope" style={{ fontSize: 11 }}></i>
              {email}
            </span>
          </div>

          {/* Alerts */}
          {error && (
            <div className="otp-alert otp-err">
              <i className="fa-solid fa-circle-exclamation" style={{ flexShrink: 0 }}></i>
              {error}
            </div>
          )}
          {resent && (
            <div className="otp-alert otp-ok">
              <i className="fa-solid fa-circle-check" style={{ flexShrink: 0 }}></i>
              New OTP sent to your email!
            </div>
          )}

          {/* OTP boxes */}
          <div className="otp-boxes" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={el => inputRefs.current[i] = el}
                className={`otp-box${digit ? ' filled' : ''}${error ? ' error' : ''}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                autoFocus={i === 0}
                disabled={loading}
              />
            ))}
          </div>

          {/* Timer */}
          <div className="otp-timer">
            {expired ? (
              <>
                <i className="fa-solid fa-circle-exclamation" style={{ color: '#fca5a5' }}></i>
                <span style={{ color: '#fca5a5', fontWeight: 700 }}>Code expired — please resend</span>
              </>
            ) : (
              <>
                <i className="fa-regular fa-clock" style={{ color: '#667eea' }}></i>
                <span>Code expires in</span>
                <span className="otp-timer-val" style={{ color: timeLeft < 60 ? '#fca5a5' : '#a5b4fc' }}>
                  {formatTime(timeLeft)}
                </span>
              </>
            )}
          </div>

          {/* Verify button */}
          <button
            className="otp-btn"
            onClick={() => handleVerify()}
            disabled={loading || !allFilled || expired}
          >
            {loading
              ? <><i className="fa-solid fa-spinner fa-spin"></i>Verifying…</>
              : <><i className="fa-solid fa-circle-check"></i>Verify OTP</>}
          </button>

          {/* Resend */}
          <div className="otp-resend">
            {expired || true ? (
              <>
                Didn't receive it?{' '}
                <button className="otp-resend-btn" onClick={handleResend} disabled={resending || (!expired && timeLeft > 540)}>
                  {resending
                    ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 4 }}></i>Sending…</>
                    : 'Resend OTP'}
                </button>
                {!expired && timeLeft > 540 && (
                  <span style={{ marginLeft: 6, fontSize: 12, color: 'rgba(255,255,255,0.2)' }}>
                    (wait {formatTime(timeLeft - 540 > 0 ? timeLeft - 540 : 0)})
                  </span>
                )}
              </>
            ) : null}
          </div>

          <Link to="/forgot-password" className="otp-back">
            <i className="fa-solid fa-arrow-left" style={{ marginRight: 5, fontSize: 11 }}></i>
            Use a different email
          </Link>

          {/* Tips */}
          <div className="otp-tips">
            <div className="otp-tips-title">
              <i className="fa-solid fa-circle-info" style={{ color: '#667eea' }}></i>
              Tips
            </div>
            <div className="otp-tips-item">
              <i className="fa-solid fa-inbox" style={{ marginRight: 6, color: '#667eea', fontSize: 11 }}></i>Check your spam/junk folder<br />
              <i className="fa-solid fa-shield-halved" style={{ marginRight: 6, color: '#667eea', fontSize: 11 }}></i>You have 3 attempts per code<br />
              <i className="fa-solid fa-clock" style={{ marginRight: 6, color: '#667eea', fontSize: 11 }}></i>Code expires after 10 minutes<br />
              <i className="fa-solid fa-paste" style={{ marginRight: 6, color: '#667eea', fontSize: 11 }}></i>You can paste the 6-digit code directly
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyOTP;